import { isNil } from '@gfe/core/utils/shared-utils';
import { addDurationFromIso, createDateTimeWithTz } from '@gfe/core/utils/time-utils';
import {
  CalendarEvent,
  CalendarRecurrence
} from '@gfe/features/components/visualization/calendar/models/calendar.models';

import { QuantifiedExpertise, Quantity } from './quantified-expertise';
import { Recurrence, Weekday, isOnceRecurrence } from './recurrence';
import { RecurringWindow } from './time-window';

export function renderQuantities(quantityVec: Quantity[]): Record<string, number> {
  const quantities: Record<string, number> = {};
  quantityVec.forEach((quant: Quantity) => {
    quantities[quant.entity_uid] = quant.value;
  });
  return quantities;
}

export function renderExpertises(
  expertiseVec: QuantifiedExpertise[]
): Record<string, number> {
  const expertises: Record<string, number> = {};
  expertiseVec.forEach((exp: QuantifiedExpertise) => {
    expertises[exp.expertise_uid] = exp.level;
  });
  return expertises;
}

export function getDateFromDateTime(dateTimeString: string): string {
  return dateTimeString.split('T')[0] || dateTimeString;
}

export function getTimeFromDateTime(dateTimeString: string): string {
  return dateTimeString.split('T')[1] || dateTimeString;
}

export function extractCustomDays(recurrence: Recurrence): number[] | undefined {
  if (typeof recurrence === 'object' && 'weekly' in recurrence) {
    return recurrence.weekly.map((weekday: Weekday) => {
      switch (weekday) {
        case 'Sun':
          return 0;
        case 'Mon':
          return 1;
        case 'Tue':
          return 2;
        case 'Wed':
          return 3;
        case 'Thu':
          return 4;
        case 'Fri':
          return 5;
        case 'Sat':
          return 6;
      }
    });
  }
  return undefined;
}

export function mapRecurrenceToCalendarRecurrence(
  recurrence: Recurrence
): CalendarRecurrence {
  if (typeof recurrence === 'object' && 'weekly' in recurrence) {
    return 'CustomDays';
  }
  // `{ once }` is handled separately by buildCalendarEvents (single dated event);
  // this fallback only guards the union exhaustively.
  if (isOnceRecurrence(recurrence)) {
    return 'Daily';
  }
  switch (recurrence) {
    case 'Daily':
      return 'Daily';
    case 'Weekdays':
      return 'Weekdays';
    case 'Weekends':
      return 'Weekends';
    default:
      return 'Daily';
  }
}

/** Time zone assumed for a site or driver that does not declare one. */
export const DEFAULT_SCHEDULE_TIME_ZONE = 'Europe/Paris';

/**
 * Places a recurring window on the calendar. The window only stores a naive time of day, so
 * `ownerTimeZone` is what anchors it to an absolute instant.
 */
export function recurringWindowTiming(
  window: RecurringWindow,
  ownerTimeZone?: string | null
): Pick<CalendarEvent, 'start' | 'end' | 'recurrence'> {
  const timeZone = ownerTimeZone ?? DEFAULT_SCHEDULE_TIME_ZONE;
  const recurrence = window.recurrence;
  const onceDate = isOnceRecurrence(recurrence) ? recurrence.once : undefined;
  const start = createDateTimeWithTz(
    onceDate ?? '2020-01-01',
    window.time_window.start,
    timeZone
  );
  return {
    start,
    end: addDurationFromIso(start, window.time_window.duration),
    recurrence: !isNil(onceDate)
      ? undefined
      : {
          type: mapRecurrenceToCalendarRecurrence(recurrence),
          customDays: extractCustomDays(recurrence),
          interval: 1,
          excludedDates: window.exceptions ?? [],
          // Clamp the rendered occurrences to [valid_from, valid_to], taken as the start and
          // end of those days in the owner's time zone. Absent bounds leave it open-ended.
          validFrom: isNil(window.valid_from)
            ? undefined
            : createDateTimeWithTz(window.valid_from, '00:00:00', timeZone),
          validTo: isNil(window.valid_to)
            ? undefined
            : createDateTimeWithTz(window.valid_to, '23:59:59', timeZone)
        }
  };
}
